/**
 * <line-item> custom element — owns the click-to-change / click-to-remove
 * behavior on a cart line. Replaces the IIFE common-line-item-controller.
 *
 * Markup contract (theme/snippets/line-item.liquid):
 *   <line-item class="line-item js-line-item …" data-line-key="…"
 *              data-quantity="N" data-hem-length="…">
 *     …
 *     <a href="/cart/change?line=N&quantity=0" class="js-remove-line-item …">
 *       Remove
 *     </a>
 *   </line-item>
 *
 * On click of any descendant `a[href*="&quantity="]`:
 *   1. Parse the line index + target quantity from the href.
 *   2. If the parent line has a hemming property, also recompute the
 *      paired chainstitch-hemming line quantity and post both updates.
 *   3. Fire the request via `core/cart-fetch.esm.js` — section rendering
 *      then drives the DOM swap, no manual list mutation.
 *
 * Source path: theme/js/components/line-item.esm.js
 * Built name : ./line-item.js
 *
 * AI: Entirely written by AI
 * Date: 2026-05-07
 * Model: Claude Opus 4.7
 *
 */

import { log } from './log.js';
import { appGet, safeAppCall } from './app-get.js';
import { changeLine, updateItems } from './cart-fetch.js';
import { getCurrentCart } from './cart-state.js';

const QUANTITY_LINK_SELECTOR = 'a[href*="&quantity="]';
const CHANGED_CLASS = 'line-item-changed';

class LineItem extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _onClick(event) {
    const link = event.target.closest(QUANTITY_LINK_SELECTOR);
    if (!link || !this.contains(link)) return;
    event.preventDefault();

    const href = link.getAttribute('href');
    const match = href.match(/\/cart\/change\?line=(\d+)&quantity=(\d+)/);
    if (!match) return;

    const line = parseInt(match[1], 10);
    const endQuantity = parseInt(match[2], 10);
    const currentQuantity = parseInt(this.getAttribute('data-quantity'), 10);
    const hemLength = this.getAttribute('data-hem-length') || '';
    const hasHem = hemLength.length > 0;
    const isRemoval = endQuantity < 1;
    const changeType = isRemoval ? 'Removed' : 'Changed';

    this.classList.add(CHANGED_CLASS);

    log('[Cart] ' + (isRemoval ? 'removing' : 'updating') + ' line item', {
      line,
      quantity: endQuantity,
    });

    safeAppCall('ga', 'send', 'event', {
      eventCategory: 'lineItem',
      eventAction: changeType,
      eventLabel: 'Line Item ' + changeType,
    });

    if (hasHem) {
      this._changeHemItem(line, hemLength, currentQuantity, endQuantity);
    } else {
      changeLine(line, endQuantity).catch((err) => {
        log('[Cart] changeLine error', err);
      });
    }
  }

  _changeHemItem(line, hemLength, currentQuantity, endQuantity) {
    const variants = appGet('data.hemProduct.variants') || [];
    const cart = getCurrentCart();
    const items = (cart && cart.items) || [];
    const hemVariant = variants.find((v) => v.title == hemLength);
    if (!hemVariant) {
      log('[Cart] no hem variant for', hemLength);
      return;
    }
    const hemInCart = items.find((i) => i.variant_id == hemVariant.id);
    if (!hemInCart) {
      // No matching hemming line; just change the parent.
      changeLine(line, endQuantity).catch((err) => log('[Cart] changeLine error', err));
      return;
    }
    const removeQuantity = hemInCart.quantity - currentQuantity;
    const hemRemoval = { [hemVariant.id]: removeQuantity };

    changeLine(line, endQuantity)
      .then(() => updateItems(hemRemoval))
      .catch((err) => log('[Cart] changeHemItem error', err));
  }
}

if (!customElements.get('line-item')) {
  customElements.define('line-item', LineItem);
}

log('[Cart] <line-item> registered');
